// Copyright 2020 Drivetrain Hub LLC
// Functions for spur and helical gear tooth equations.
// Counterpart to dh-gear tooth_eqn module.

import math from "./math";
import mesh from "./mesh";
import {makeFloats} from "./utils";

// region TOOTH PARAMS

function coefficient(val, moduleNormal) {
  return val / moduleNormal;
}

function fromCoefficient(valCoefficient, moduleNormal) {
  return valCoefficient * moduleNormal;
}

function moduleNormal1({moduleTransverse, helixAngle}) {
  return moduleTransverse * Math.cos(helixAngle);
}

function moduleNormal2(pitchNormal) {
  return pitchNormal / Math.PI;
}

function moduleTransverse1({moduleNormal, helixAngle}) {
  return moduleNormal / Math.cos(helixAngle)
}

function moduleTransverse2({theoreticalPitchDiameter, numberOfTeeth}) {
  return theoreticalPitchDiameter / numberOfTeeth;
}

function theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse}) {
  return numberOfTeeth * moduleTransverse
}

function theoreticalPitchDiameter2({pitchNormal, numberOfTeeth, helixAngle}) {
  return pitchNormal * numberOfTeeth / (Math.PI * Math.cos(helixAngle))
}

function pressureAngleNormal1(pressureAngleTransverse, helixAngle) {
  return Math.atan(Math.tan(pressureAngleTransverse) * Math.cos(helixAngle))
}

/**
 * Calculate the transverse pressure angle at the theoretical pitch diameter.
 * @param {number} pressureAngleNormal - Pressure angle at theoretical pitch diameter, [rad].
 * @param {number} helixAngle - Helix angle at theoretical pitch diameter, [rad].
 * @return {number} Transverse pressure angle at theoretical pitch diameter, [rad].
 */
function pressureAngleTransverse1({pressureAngleNormal, helixAngle}) {
  return Math.atan(Math.tan(pressureAngleNormal) / Math.cos(helixAngle));
}

function pressureAngleTransverse2(baseDiameter, diameter) {
  // Calculate the transverse pressure angle at an arbitrary diameter.
  return Math.acos(baseDiameter / diameter)
}

function pitchNormal1(moduleNormal) {
  return Math.PI * moduleNormal;
}

function pitchNormal2({basePitchNormal, pressureAngleNormal}) {
  return basePitchNormal / Math.cos(pressureAngleNormal)
}

function basePitchNormal1({pitchNormal, pressureAngleNormal}) {
  return pitchNormal * Math.cos(pressureAngleNormal)
}

function basePitchTransverse1({baseDiameter, numberOfTeeth}) {
  return baseDiameter * Math.PI / numberOfTeeth;
}

/**
 * Calculate transverse pitch angle of gear teeth.  This is the transverse angle between successive teeth.
 * @param {number} numberOfTeeth - Number of teeth.
 * @return {number} Transverse pitch angle, [rad].
 */
function pitchAngleTransverse1(numberOfTeeth) {
  return 2 * Math.PI / numberOfTeeth;
}

/**
 * Calculate involute base diameter.
 * @param {number} theoreticalPitchDiameter - Theoretical pitch diameter.
 * @param {number} pressureAngleTransverse - Transverse pressure angle at theoretical pitch diameter, [rad].
 * @return {number} Involute base diameter.
 */
function baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse}) {
  return theoreticalPitchDiameter * Math.cos(pressureAngleTransverse);
}

function baseDiameter2({moduleNormal, pressureAngleNormal, numberOfTeeth, helixAngle}) {
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = pressureAngleTransverse1({pressureAngleNormal, helixAngle});

  return baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
}

function toothThicknessNormal2({moduleNormal, pressureAngleNormal, profileShiftCoefficient}) {
  return moduleNormal * (Math.PI / 2 + 2 * profileShiftCoefficient * Math.tan(pressureAngleNormal))
}

function toothThicknessTransverse1({toothThicknessNormal, helixAngle}) {
  return toothThicknessNormal / Math.cos(helixAngle)
}

function toothThicknessTransverse2({moduleNormal, pressureAngleNormal, helixAngle, profileShiftCoefficient}) {
  const toothThicknessNormal = toothThicknessNormal2({moduleNormal, pressureAngleNormal, profileShiftCoefficient});
  return toothThicknessTransverse1({toothThicknessNormal, helixAngle});
}

function toothThicknessTransverse3(
  diameter, {moduleNormal, pressureAngleNormal, numberOfTeeth, helixAngle, profileShiftCoefficient}) {
  const paTransverse = pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = baseDiameter2({moduleNormal, pressureAngleNormal, numberOfTeeth, helixAngle});
  const paTransverseAtDiameter = pressureAngleTransverse2(baseDiameter, diameter);

  return diameter * (Math.PI / (2 * numberOfTeeth)
    + 2 * profileShiftCoefficient * Math.tan(pressureAngleNormal) / numberOfTeeth +
    involuteFcn(paTransverse) - involuteFcn(paTransverseAtDiameter));
}

/**
 * Calculate the transverse tooth thickness half angle.
 * @param {number} diameter - Arbitrary diameter.
 * @param {number} toothThicknessTransverseAtDiameter - Transverse tooth thickness at specified diameter.
 * @return {number} Transverse tooth thickness half angle.
 */
function toothHalfAngleTransverse1(diameter, toothThicknessTransverseAtDiameter) {
  return toothThicknessTransverseAtDiameter / diameter;
}

/**
 * Calculate the transverse tooth thickness half angle at the base diameter.
 * @param {number} theoreticalPitchDiameter - Theoretical pitch diameter.
 * @param {number} pressureAngleTransverse - Transverse pressure angle at reference diameter.
 * @param {number} toothThicknessTransverse - Transverse tooth thickness at reference diameter.
 * @return {number} Transverse tooth thickness half angle at the base diameter.
 */
function toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse}) {
  return involuteFcn(pressureAngleTransverse) + toothHalfAngleTransverse1(theoreticalPitchDiameter, toothThicknessTransverse);
}

function helixAngleBase1({pressureAngleTransverse, helixAngle}) {
  return Math.atan(Math.cos(pressureAngleTransverse) * Math.tan(helixAngle));
}

function rootDiameter1({theoreticalPitchDiameter, basicRackDedendum, profileShift}) {
  return theoreticalPitchDiameter - 2 * (basicRackDedendum - profileShift);
}

function rootDiameter2({theoreticalPitchDiameter, toolAddendum, profileShift}) {
  // Tooth root diameter resulting from the cutter tool tip.
  return theoreticalPitchDiameter - 2 * (toolAddendum - profileShift);
}

function rootDiameter3({theoreticalPitchDiameter, toothDedendum}) {
  // Tooth root diameter per a known tooth dedendum (not coefficient).

  return theoreticalPitchDiameter - 2 * toothDedendum;
}

function tipDiameter1({theoreticalPitchDiameter, basicRackAddendum, profileShift}) {
  return theoreticalPitchDiameter + 2 * (basicRackAddendum + profileShift);
}

function tipDiameter2({theoreticalPitchDiameter, toolDedendum, profileShift}) {
  // Tooth tip diameter resulting from a *topping* cutter, i.e. bottom land of tool performs cutting.
  return theoreticalPitchDiameter + 2 * (toolDedendum + profileShift);
}

function tipDiameter3({theoreticalPitchDiameter, toothAddendum}) {
  // Tooth tip diameter per a known tooth addendum (not coefficient).

  return theoreticalPitchDiameter + 2 * toothAddendum
}

// endregion

// region INVOLUTE MATH AND CONVERSIONS

/**
 * Calculate the involute function of an angle.
 * @param {number} phi - Angle, [rad].
 * @return {number} Involute function of angle phi.
 */
function involuteFcn(phi) {
  return Math.tan(phi) - phi;
}

function inverseInvoluteFcn(involuteFcnValue) {
  const q = involuteFcnValue;
  return (3 * q) ** (1 / 3) -
    (2 * q) / 5 +
    (9 / 175) * (3) ** (2 / 3) * (q) ** (5 / 3) -
    (2 / 175) * (3) ** (1 / 3) * (q) ** (7 / 3) -
    (144 / 67375) * (q) ** (9 / 3) +
    (3258 / 3128125) * (3) ** (2 / 3) * (q) ** (11 / 3) -
    (49711 / 153278125) * (3) ** (1 / 3) * (q) ** (13 / 3);
}

/**
 * Convert a value in the normal plane to its value in the transverse plane.
 * @param {number} valNormal - Value in the normal plane.
 * @param {number} helixAngle - Helix angle at the specified normal value.
 * @return {number} Transverse value.
 */
function normal2Transverse(valNormal, helixAngle) {
  return valNormal / Math.cos(helixAngle)
}

/**
 * Convert a value in the transverse plane to its value in the normal plane.
 * @param {number} valTransverse - Value in the transverse plane.
 * @param {number} helixAngle - Helix angle at the specified transverse value.
 * @return {number} Normal value.
 */
function transverse2Normal(valTransverse, helixAngle) {
  return valTransverse * Math.cos(helixAngle)
}

/**
 * Convert involute diameter to roll angle.
 * @param {number} baseDiameter - Involute base circle diameter.
 * @param {number} diameter - Arbitrary diameter.
 * @return {number} Roll angle, [rad].
 */
function diameter2RollAngle(baseDiameter, diameter) {
  return Math.tan(Math.acos(baseDiameter/ diameter));
}

function rollAngle2Diameter(baseDiameter, rollAngle) {
  return baseDiameter / Math.cos(Math.atan(rollAngle));
}

function rollAngle2RollDistance(baseDiameter, rollAngle) {
  return rollAngle * baseDiameter / 2;
}

function rollDistance2RollAngle(baseDiameter, rollDistance) {
  return 2 * rollDistance / baseDiameter;
}

function diameter2RollDistance(baseDiameter, diameter) {
  const rollAngle = diameter2RollAngle(baseDiameter, diameter);
  return rollAngle2RollDistance(baseDiameter, rollAngle);
}

function rollDistance2Diameter(baseDiameter, rollDistance) {
  const rollAngle = rollDistance2RollAngle(baseDiameter, rollDistance);
  return rollAngle2Diameter(baseDiameter, rollAngle)
}

function diameter2PressureAngleTransverse(baseDiameter, diameter) {
  return Math.acos(baseDiameter / diameter);
}

/**
 * Calculate Bezier curve coefficients for involute approximation.
 * References:
 *  1. F.Higuchi, S. Gofuku, T. Maekawa, H. Mukundan, N.M. Patrikalakis, "Approximation of Involute Curves for CAD-System Processing" YNU Digital Eng Lab Memorandum 05-1 2006.
 * @param {number} radiusBase - Involute base radius.
 * @param {number} rollAngleStart - Start of involute roll angle.
 * @param {number} rollAngleEnd - End of involute roll angle.
 * @param {number} order - Order of Bezier curve.  HTML5 only supports quadratic (2) and cubic (3) Bezier curves.
 * @return {Array} Array of objects with 'x' and 'y' properties for Bezier control point coordinates.
 */
function involuteBezierCoeffs(radiusBase, rollAngleStart, rollAngleEnd, order = 3) {

  function chebyExpnCoeffs(j, func)
  {
    const N = 50;  // a suitably large number  N>>p
    let c = 0, k;

    for (k=1; k<=N; k++)
    {
      c += func(Math.cos(Math.PI*(k-0.5)/N)) * Math.cos(Math.PI*j*(k-0.5)/N);
    }
    return 2 * c / N;
  }

  function chebyPolyCoeffs(p, func)
  {
    let coeffs = [],
      fnCoeff = [],
      T = [[], []],
      i, j, k, pwr;

    // populate 1st 2 rows of T
    for (i=0; i<p+1; i++)
    {
      T[0][i] = 0;
      T[1][i] = 0;
    }
    T[0][0] = 1;
    T[1][1] = 1;
    /* now generate the Chebyshev polynomial coefficient using
       formula T(k+1) = 2xT(k) - T(k-1) which yields
    T = [ [ 1,  0,  0,  0,  0,  0],    // T0(x) =  +1
          [ 0,  1,  0,  0,  0,  0],    // T1(x) =   0  +x
          [-1,  0,  2,  0,  0,  0],    // T2(x) =  -1  0  +2xx
          [ 0, -3,  0,  4,  0,  0],    // T3(x) =   0 -3x    0   +4xxx
          [ 1,  0, -8,  0,  8,  0],    // T4(x) =  +1  0  -8xx       0  +8xxxx
          [ 0,  5,  0,-20,  0, 16],    // T5(x) =   0  5x    0  -20xxx       0  +16xxxxx
          ...                     ];
    */
    for (k=1; k<p+1; k++)
    {
      T[k+1] = [0];
      for (j=0; j<T[k].length-1; j++)
      {
        T[k+1][j+1] = 2*T[k][j];
      }
      for (j=0; j<T[k-1].length; j++)
      {
        T[k+1][j] -= T[k-1][j];
      }
    }
    // convert the chebyshev function series into a simple polynomial
    // and collect like terms, out T polynomial coefficients
    for (k=0; k<=p; k++)
    {
      fnCoeff[k] = chebyExpnCoeffs(k, func);
      coeffs[k] = 0;
    }
    for (k=0; k<=p; k++)
    {
      for (pwr=0; pwr<=p; pwr++)    // loop thru powers of x
      {
        coeffs[pwr] += fnCoeff[k]*T[k][pwr];
      }
    }
    coeffs[0] -= chebyExpnCoeffs(0, func)/2;  // fix the 0th coeff

    return coeffs;
  }

  // Equation of involute using the Bezier parameter t as variable
  function involuteXbez(t)
  {
    // map t (0 <= t <= 1) onto x (where -1 <= x <= 1)
    const x = t*2-1;
    //map theta (where ts <= theta <= te) from x (-1 <=x <= 1)
    const theta = x*(rollAngleEnd-rollAngleStart)/2 + (rollAngleStart + rollAngleEnd)/2;
    return radiusBase*(Math.cos(theta)+theta*Math.sin(theta));
  }

  function involuteYbez(t)
  {
    // map t (0 <= t <= 1) onto x (where -1 <= x <= 1)
    const x = t*2-1,
      //map theta (where ts <= theta <= te) from x (-1 <=x <= 1)
      theta = x*(rollAngleEnd-rollAngleStart)/2 + (rollAngleStart + rollAngleEnd)/2;

    return radiusBase*(Math.sin(theta)-theta*Math.cos(theta));
  }

  function binom(n, k)
  {
    let coeff = 1,
      i;

    for (i = n-k+1; i <= n; i++)
    {
      coeff *= i;
    }
    for (i = 1; i <= k; i++)
    {
      coeff /= i;
    }

    return coeff;
  }

  function bezCoeff(i, func)
  {
    // generate the polynomial coeffs in one go
    const polyCoeffs = chebyPolyCoeffs(order, func);
    let bc, j;

    for (bc=0, j=0; j<=i; j++)
    {
      bc += binom(i,j)*polyCoeffs[j] / binom(order,j);
    }
    return bc;
  }

  // calculate Bezier coefficients

  let bzCoeffs = [];
  let bcoeff;

  for (let i=0; i<=order; i++) {
    bcoeff = {};
    bcoeff.x = bezCoeff(i, involuteXbez);
    bcoeff.y = bezCoeff(i, involuteYbez);
    bzCoeffs.push(bcoeff);
  }

  return bzCoeffs;
}

/**
 * Calculate the range of polar angles for the axial length and helix angle.
 * @param length - Axial length of helix curve to consider.
 * @param {number} helixRadius - Helix radius at the specified angle.
 * @param {number} helixAngle - Helix angle at the specified radius.
 * @return {{phi: number, b: number}} - Polar angle range and length/phi.
 */
function helixRotation(length, helixAngle, helixRadius) {
  // REFERENCE ONLY:
  // const helixPitch = 2 * Math.PI * b;
  // length = helixPitch / 4;
  // slope = b / helixRadius;

  const b = helixRadius / Math.tan(helixAngle);
  const phi = length / b;

  return {phi, b}
}

// endregion

// region TOOLING

function toothThicknessTransverseFinish1({toothThicknessTransverse, finishStockTransverse}) {
  return toothThicknessTransverse - 2 * finishStockTransverse;
}

function toothThicknessNormalFinish1({helixAngle, toothThicknessNormal, finishStockNormal}) {
  const toothThicknessTransverse = normal2Transverse(toothThicknessNormal, helixAngle);
  const finishStockTransverse = normal2Transverse(finishStockNormal, helixAngle);
  const toothThicknessTransverseFinish = toothThicknessTransverseFinish1({toothThicknessTransverse, finishStockTransverse});
  return transverse2Normal(toothThicknessTransverseFinish, helixAngle)
}

function finishStockTransverse1({toothThicknessTransverse, toothThicknessTransverseFinish}) {
  return (toothThicknessTransverse - toothThicknessTransverseFinish) / 2;
}

function finishStockNormal1({helixAngle, toothThicknessNormal, toothThicknessNormalFinish}) {
  const toothThicknessTransverse = toothThicknessTransverse1({toothThicknessNormal, helixAngle});
  const toothThicknessTransverseFinish = toothThicknessTransverse1({toothThicknessNormal: toothThicknessNormalFinish, helixAngle});
  const finishStockTransverse = finishStockTransverse1({toothThicknessTransverse, toothThicknessTransverseFinish});
  return transverse2Normal(finishStockTransverse, helixAngle)
}

function toothAddendum1({theoreticalPitchDiameter, tipDiameter}) {
  return (tipDiameter - theoreticalPitchDiameter) / 2;
}

function toothDedendum1({theoreticalPitchDiameter, rootDiameter}) {
  return (theoreticalPitchDiameter - rootDiameter) / 2;
}

function basicRackAddendum1({theoreticalPitchDiameter, tipDiameter, profileShift}) {
  return (tipDiameter - theoreticalPitchDiameter) / 2 - profileShift;
}

function basicRackDedendum1({theoreticalPitchDiameter, rootDiameter, profileShift}) {
  return (theoreticalPitchDiameter - rootDiameter) / 2 + profileShift;
}

function profileShiftCoefficient1({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessTransverse}) {
  return (toothThicknessTransverse * Math.cos(helixAngle) / moduleNormal - Math.PI / 2) / (2 * Math.tan(pressureAngleNormal))
}

function profileShiftCoefficient2({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessNormal}) {
  const toothThicknessTransverse = toothThicknessTransverse1({toothThicknessNormal, helixAngle});
  return profileShiftCoefficient1({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessTransverse})
}

function profileShiftCoefficient3({numberOfTeeth, pressureAngleNormal, helixAngle, toothThicknessNormal},
                                  toothThicknessDiameter, pressureAngleNormalAtToothThickness) {
  const ttTransverse = toothThicknessTransverse1({toothThicknessNormal, helixAngle});
  const paTransverse = pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const paTransverseAtToothThickness = pressureAngleTransverse1({
    pressureAngleNormal: pressureAngleNormalAtToothThickness,
    helixAngle
  });
  return numberOfTeeth * (ttTransverse / toothThicknessDiameter -
    Math.PI / (2 * numberOfTeeth) - involuteFcn(paTransverse) + involuteFcn(paTransverseAtToothThickness)) /
    (2 * Math.tan(pressureAngleNormal));
}

function genProfileShiftCoefficient1({numberOfTeeth, genPressureAngleNormal, genHelixAngle, toothThicknessNormal,
                                       theoreticalPitchDiameter, genTheoreticalPitchDiameter}) {
  const genPressureAngleTransverse = pressureAngleTransverse1({
    pressureAngleNormal: genPressureAngleNormal,
    helixAngle: genHelixAngle
  });
  const genBaseDiameter = baseDiameter1({
    theoreticalPitchDiameter: genTheoreticalPitchDiameter,
    pressureAngleTransverse: genPressureAngleTransverse
  });
  const genPressureAngleTransverseAtRef = pressureAngleTransverse2(genBaseDiameter, theoreticalPitchDiameter);
  const genPressureAngleNormalAtRef = pressureAngleNormal1(genPressureAngleTransverseAtRef, genHelixAngle);

  return profileShiftCoefficient3({numberOfTeeth, pressureAngleNormal: genPressureAngleNormal,
      helixAngle: genHelixAngle, toothThicknessNormal}, theoreticalPitchDiameter, genPressureAngleNormalAtRef);
}

function genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter}) {
  return Math.asin(genModuleNormal * numberOfTeeth * Math.tan(helixAngle) / theoreticalPitchDiameter);
}

// HANDLING FORM DATA

/**
 * Calculate generating tool data from reference tooth data.
 * @param object {object} - Reference tooth data.
 * @param genPressureAngleNormal {number} - Normal pressure angle of *generating tool*.
 * @param unitAngle {string} - Angular units, 'rad' or 'deg', of reference tooth data.  Output units are consistent.
 * @return {{moduleNormal: *, helixAngle: number, pressureAngleNormal: *, addendumCoefficient: *, profileShiftCoefficient: *, workPieceDiameter: *, dedendumCoefficient: *}} Generating tool data.
 */
function generatingRackData(
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter},
  genPressureAngleNormal, unitAngle) {
  // args: Reference tooth data and generating pressure angle.
  // return: Generating tool data.

  // parse input args (str-to-num); alternative method: [a, b] = [a, b].map(Number)
  ({moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter, genPressureAngleNormal} = makeFloats({
    moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter, genPressureAngleNormal}
  ));

  // default generating pressure angle
  if (Number.isNaN(genPressureAngleNormal)) genPressureAngleNormal = pressureAngleNormal;

  // convert params with angular units; assumed to be input as degree, not radian
  genPressureAngleNormal = math.angle2rad(genPressureAngleNormal, unitAngle);
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // define variables for generation tooling
  let genModuleNormal, genHelixAngle, genProfileShiftCoefficient;
  let genAddendumCoefficient, genDedendumCoefficient, genTheoreticalPitchDiameter;

  // calculate reference tooling params
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const profileShiftCoefficient = profileShiftCoefficient2({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessNormal});

  if (genPressureAngleNormal === pressureAngleNormal) {
    // generating tool uses reference lead
    genModuleNormal = moduleNormal;
    genHelixAngle = helixAngle;
    genProfileShiftCoefficient = profileShiftCoefficient;
    genTheoreticalPitchDiameter = theoreticalPitchDiameter;
  } else {
    // generating tool uses short or long lead; aka short or long pitch

    // base pitch constraint
    const pitchNormal = pitchNormal1(moduleNormal);
    const basePitchNormal = basePitchNormal1({pitchNormal, pressureAngleNormal});
    const genPitchNormal = pitchNormal2({basePitchNormal, pressureAngleNormal: genPressureAngleNormal});
    genModuleNormal = moduleNormal2(genPitchNormal);

    // helix lead constraint
    genHelixAngle = genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter});
    genTheoreticalPitchDiameter = theoreticalPitchDiameter2({pitchNormal: genPitchNormal, numberOfTeeth, helixAngle: genHelixAngle});

    // tooth thickness constraint; profile shift correction
    genProfileShiftCoefficient = genProfileShiftCoefficient1({numberOfTeeth, genPressureAngleNormal, genHelixAngle,
      toothThicknessNormal, theoreticalPitchDiameter, genTheoreticalPitchDiameter})
  }

  const genProfileShift = fromCoefficient(genProfileShiftCoefficient, genModuleNormal);

  // tool height constraint
  // tool addendum; inverse of basic rack
  const genAddendum = basicRackDedendum1({
    theoreticalPitchDiameter: genTheoreticalPitchDiameter,
    profileShift: genProfileShift,
    rootDiameter});

  // tool dedendum; inverse of basic rack; assumes topping cutter
  const genDedendum = basicRackAddendum1({
    theoreticalPitchDiameter: genTheoreticalPitchDiameter,
    profileShift: genProfileShift,
    tipDiameter});

  genAddendumCoefficient = coefficient(genAddendum, genModuleNormal);
  genDedendumCoefficient = coefficient(genDedendum, genModuleNormal);

  // convert output units to match input units
  if (unitAngle === 'deg') {
    genPressureAngleNormal = math.rad2deg(genPressureAngleNormal);
    genHelixAngle = math.rad2deg(genHelixAngle);
  }

  return {
    moduleNormal: genModuleNormal,
    pressureAngleNormal: genPressureAngleNormal,
    helixAngle: genHelixAngle,
    profileShiftCoefficient: genProfileShiftCoefficient,
    addendumCoefficient: genAddendumCoefficient,
    dedendumCoefficient: genDedendumCoefficient,
  }
}

function generatingPinionData(
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter},
  {genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient, cutterDedendumCoefficient},
  internal, unitAngle) {
  // args: {ref tooth data}, {cutter tool data}
  // cutterDedendumCoefficient is optional, assuming topping cutter otherwise.
  // Arguments are anticipated as being strings, but numbers will work too.
  // Internal props can be specified as positive or negative.
  // Calculated center distances are negative for internal gears.
  // For reference, see dh_gear.PinionCutter.from_tooth_params()

  // parse input args (str-to-num); alternative method: [a, b] = [a, b].map(Number)
  ({moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter,
    genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient, cutterDedendumCoefficient} = makeFloats({
    moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, toothThicknessNormal, rootDiameter, tipDiameter,
    genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient, cutterDedendumCoefficient}
  ));

  // internal gear; make *negative* for calculations
  if (internal) {
    numberOfTeeth = -Math.abs(numberOfTeeth);
    rootDiameter = -Math.abs(rootDiameter);
    tipDiameter = -Math.abs(tipDiameter);
  }

  // default generating pressure angle
  if (Number.isNaN(genPressureAngleNormal)) genPressureAngleNormal = pressureAngleNormal;

  // convert params with angular units; assumed to be input as degree, not radian
  genPressureAngleNormal = math.angle2rad(genPressureAngleNormal, unitAngle);
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // calculate reference tooling params
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});

  // variables for generation tooling
  let genModuleNormal, genHelixAngle, cutterHelixAngle, genProfileShiftCoefficient;
  const isReferenceLead = genPressureAngleNormal === pressureAngleNormal;

  if (isReferenceLead) {
    // generating tool uses reference lead
    genModuleNormal = moduleNormal;
    genHelixAngle = helixAngle;
    genProfileShiftCoefficient = profileShiftCoefficient2({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessNormal});
  } else {
    // generating tool uses short or long lead; aka short or long pitch

    // base pitch constraint
    const pitchNormal = pitchNormal1(moduleNormal);
    const basePitchNormal = basePitchNormal1({pitchNormal, pressureAngleNormal});
    const genPitchNormal = pitchNormal2({basePitchNormal, pressureAngleNormal: genPressureAngleNormal});
    genModuleNormal = moduleNormal2(genPitchNormal);

    // helix lead constraint; generated tooth, not cutter tooth
    genHelixAngle = genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter});
  }

  // cutter params
  cutterHelixAngle = numberOfTeeth > 0 ? -genHelixAngle : genHelixAngle;
  const genPressureAngleTransverse = pressureAngleTransverse1({
    pressureAngleNormal: genPressureAngleNormal,
    helixAngle: genHelixAngle,
  });
  const genModuleTransverse = moduleTransverse1({
    moduleNormal: genModuleNormal,
    helixAngle: genHelixAngle,
  });

  // tooth thickness constraint; ref tooth == gen tooth @ ref dia
  if (!isReferenceLead) {
    const genTheoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse: genModuleTransverse});
    genProfileShiftCoefficient = genProfileShiftCoefficient1({
      numberOfTeeth,
      genPressureAngleNormal,
      genHelixAngle,
      toothThicknessNormal,
      theoreticalPitchDiameter,
      genTheoreticalPitchDiameter
    });
  }
  const cutterToothThicknessNormal = toothThicknessNormal2({
    moduleNormal: genModuleNormal,
    pressureAngleNormal: genPressureAngleNormal,
    profileShiftCoefficient: cutterProfileShiftCoefficient
  });

  // compute tight mesh params; meshing of cutter and *generated* tooth
  let workingPressureAngleTransverse = mesh.workingPressureAngleTransverseTheoretical1({
    x1: cutterProfileShiftCoefficient,
    x2: genProfileShiftCoefficient,
    z1: cutterNumberOfTeeth,
    z2: numberOfTeeth,
    alphaN: genPressureAngleNormal,
    alphaT: genPressureAngleTransverse,
  });
  let referenceCenterDistance = mesh.centerDistanceReference2({
    moduleTransverse: genModuleTransverse,
    z1: cutterNumberOfTeeth,
    z2: numberOfTeeth
  });
  let workingCenterDistance = mesh.centerDistanceTheoretical1({
    referenceCenterDistance,
    workingPressureAngleTransverse,
    pressureAngleTransverse: genPressureAngleTransverse,
  });

  // DEV ONLY
  // console.log('genModuleNormal', genModuleNormal);
  // console.log('genModuleTransverse', genModuleTransverse);
  // console.log('cutterNumberOfTeeth', cutterNumberOfTeeth);
  // console.log('numberOfTeeth', numberOfTeeth);
  // console.log('genPressureAngleNormal', genPressureAngleNormal);
  // console.log('genPressureAngleTransverse', genPressureAngleTransverse);
  // console.log('workingPressureAngleTransverse', workingPressureAngleTransverse);
  // console.log('referenceCenterDistance', referenceCenterDistance);

  // cutter params
  const cutterTheoreticalPitchDiameter = theoreticalPitchDiameter1({
    numberOfTeeth: cutterNumberOfTeeth,
    moduleTransverse: genModuleTransverse
  });

  // tool addendum
  const cutterTipDiameter = 2 * workingCenterDistance - rootDiameter;
  const cutterAddendum = toothAddendum1({
    theoreticalPitchDiameter: cutterTheoreticalPitchDiameter,
    tipDiameter: cutterTipDiameter
  });
  const cutterAddendumCoefficient = coefficient(cutterAddendum, genModuleNormal);

  // tool dedendum
  let cutterDedendum;
  if (Number.isNaN(cutterDedendumCoefficient)) {
    // assume topping cutter
    const cutterRootDiameter = 2 * workingCenterDistance - tipDiameter;
    cutterDedendum = toothDedendum1({
      theoreticalPitchDiameter: cutterTheoreticalPitchDiameter,
      rootDiameter: cutterRootDiameter,
    });
    cutterDedendumCoefficient = coefficient(cutterDedendum, genModuleNormal);
  } else {
    // cutter dedendum specified
    cutterDedendum = fromCoefficient(cutterDedendumCoefficient, genModuleNormal);
  }

  const cutterRootDiameter = rootDiameter3({
    theoreticalPitchDiameter: cutterTheoreticalPitchDiameter,
    toothDedendum: cutterDedendum
  });

  // convert output units to match input units
  if (unitAngle === 'deg') {
    cutterHelixAngle = math.rad2deg(cutterHelixAngle);
    workingPressureAngleTransverse = math.rad2deg(workingPressureAngleTransverse);
  }

  return {
    addendumCoefficient: cutterAddendumCoefficient,
    dedendumCoefficient: cutterDedendumCoefficient,
    moduleNormal: genModuleNormal,
    helixAngle: cutterHelixAngle,
    toothThicknessNormal: cutterToothThicknessNormal,
    rootDiameter: cutterRootDiameter,
    tipDiameter: cutterTipDiameter,
    workingPressureAngleTransverse,
    workingCenterDistance,
    referenceCenterDistance
  }
}

function referenceToothFromRack1(
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, workPieceDiameter},
  {genPressureAngleNormal, genProfileShiftCoefficient, genAddendumCoefficient, genDedendumCoefficient}, unitAngle) {
  // Computes reference tooth params given certain generating rack data.
  // Tooth thickness is defined by the generating profile shift.
  // args: {reference tooth data}, {generating rack data}
  // return: Reference tooth data.

  // parse input args (str-to-num); alternative method: [a, b] = [a, b].map(Number)
  ({moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, workPieceDiameter,
    genPressureAngleNormal, genProfileShiftCoefficient, genAddendumCoefficient, genDedendumCoefficient} = makeFloats({
    moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, workPieceDiameter,
    genPressureAngleNormal, genProfileShiftCoefficient, genAddendumCoefficient, genDedendumCoefficient}
  ));

  // default generating pressure angle
  if (Number.isNaN(genPressureAngleNormal)) genPressureAngleNormal = pressureAngleNormal;

  // convert params with angular units; assumed to be input as degree, not radian
  genPressureAngleNormal = math.angle2rad(genPressureAngleNormal, unitAngle);
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // reference tooth data
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});

  // base pitch constraint
  const pitchNormal = pitchNormal1(moduleNormal);
  const basePitchNormal = basePitchNormal1({pitchNormal, pressureAngleNormal});
  const genPitchNormal = pitchNormal2({basePitchNormal, pressureAngleNormal: genPressureAngleNormal});
  const genModuleNormal = moduleNormal2(genPitchNormal);

  // helix lead constraint
  const genHelixAngle = genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter});
  const genTheoreticalPitchDiameter = theoreticalPitchDiameter2({pitchNormal: genPitchNormal, numberOfTeeth, helixAngle: genHelixAngle});

  // generating tool params
  const toolAddendum = genAddendumCoefficient * genModuleNormal;
  const toolDedendum = genDedendumCoefficient * genModuleNormal;
  const genProfileShift = genProfileShiftCoefficient * genModuleNormal;

  const rootDiameter = rootDiameter2({theoreticalPitchDiameter: genTheoreticalPitchDiameter, toolAddendum, profileShift: genProfileShift});
  const toppingDiameter = tipDiameter2({theoreticalPitchDiameter: genTheoreticalPitchDiameter, toolDedendum, profileShift: genProfileShift});
  const tipDiameter = workPieceDiameter < toppingDiameter ? workPieceDiameter : toppingDiameter;
  // ==> alternatively, calculate from reference params

  // tooth thickness constraint; ref tooth thickness_n == gen tooth thickness_n @ ref diameter
  const genToothThicknessTransverseAtRef = toothThicknessTransverse3(theoreticalPitchDiameter,
    {moduleNormal: genModuleNormal, pressureAngleNormal: genPressureAngleNormal, numberOfTeeth: numberOfTeeth,
      helixAngle: genHelixAngle, profileShiftCoefficient: genProfileShiftCoefficient});

  const toothThicknessNormal = transverse2Normal(genToothThicknessTransverseAtRef, genHelixAngle);
  const profileShiftCoefficient = profileShiftCoefficient2({moduleNormal, pressureAngleNormal, helixAngle, toothThicknessNormal});

  // DEV ONLY
  // console.log('profileShiftCoefficient', profileShiftCoefficient);
  // console.log('genProfileShiftCoefficient', genProfileShiftCoefficient);

  return {
    tipDiameter: tipDiameter,
    rootDiameter: rootDiameter,
    toothThicknessNormal: toothThicknessNormal,
    profileShiftCoefficient: profileShiftCoefficient,
  }
}

function referenceToothFromRack2(
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter},
  {genPressureAngleNormal, genAddendumCoefficient, genDedendumCoefficient}, unitAngle) {
  // Computes reference tooth params given certain generating rack data.
  // Tooth thickness is defined by the reference profile shift.
  // args: {reference tooth data}, {generating rack data}
  // return: Reference tooth data.

  // parse input args (str-to-num); alternative method: [a, b] = [a, b].map(Number)
  ({moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter,
    genPressureAngleNormal, genAddendumCoefficient, genDedendumCoefficient} = makeFloats({
    moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter,
    genPressureAngleNormal, genAddendumCoefficient, genDedendumCoefficient}
  ));

  // default generating pressure angle
  if (Number.isNaN(genPressureAngleNormal)) genPressureAngleNormal = pressureAngleNormal;

  // convert params with angular units; assumed to be input as degree, not radian
  genPressureAngleNormal = math.angle2rad(genPressureAngleNormal, unitAngle);
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // reference tooth data
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const toothThicknessNormal = toothThicknessNormal2({moduleNormal, pressureAngleNormal, profileShiftCoefficient});

  // base pitch constraint
  const pitchNormal = pitchNormal1(moduleNormal);
  const basePitchNormal = basePitchNormal1({pitchNormal, pressureAngleNormal});
  const genPitchNormal = pitchNormal2({basePitchNormal, pressureAngleNormal: genPressureAngleNormal});
  const genModuleNormal = moduleNormal2(genPitchNormal);

  // helix lead constraint
  const genHelixAngle = genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter});
  const genTheoreticalPitchDiameter = theoreticalPitchDiameter2({pitchNormal: genPitchNormal, numberOfTeeth, helixAngle: genHelixAngle});

  // generating tool params
  const genProfileShiftCoefficient = genProfileShiftCoefficient1({
    numberOfTeeth, genPressureAngleNormal, genHelixAngle, toothThicknessNormal,
    theoreticalPitchDiameter, genTheoreticalPitchDiameter
  });
  const genProfileShift = genProfileShiftCoefficient * genModuleNormal;
  const toolAddendum = genAddendumCoefficient * genModuleNormal;
  const toolDedendum = genDedendumCoefficient * genModuleNormal;

  const rootDiameter = rootDiameter2({theoreticalPitchDiameter: genTheoreticalPitchDiameter, toolAddendum, profileShift: genProfileShift});
  const toppingDiameter = tipDiameter2({theoreticalPitchDiameter: genTheoreticalPitchDiameter, toolDedendum, profileShift: genProfileShift});
  const tipDiameter = workPieceDiameter < toppingDiameter ? workPieceDiameter : toppingDiameter;

  return {
    tipDiameter: tipDiameter,
    rootDiameter: rootDiameter,
    toothThicknessNormal: toothThicknessNormal
  }
}

function referenceToothFromPinion1(
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter},
  {genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient,
    cutterAddendumCoefficient, cutterDedendumCoefficient}, internal, unitAngle) {
  // Computes reference tooth params given certain generating pinion data.
  // Tooth thickness is defined by the reference profile shift.
  // Be careful not to confuse "generated tooth" vs "cutter data", despite some params being equal.
  // args: {reference tooth data}, {generating pinion data}
  // return: Reference tooth data.

  // parse input args (str-to-num); alternative method: [a, b] = [a, b].map(Number)
  ({moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter,
    genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient,
    cutterAddendumCoefficient, cutterDedendumCoefficient} = makeFloats({
    moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, profileShiftCoefficient, workPieceDiameter,
    genPressureAngleNormal, cutterNumberOfTeeth, cutterProfileShiftCoefficient,
    cutterAddendumCoefficient, cutterDedendumCoefficient}
  ));

  // internal gear; make *negative* for calculations
  if (internal) {
    numberOfTeeth = -numberOfTeeth;
    workPieceDiameter = -workPieceDiameter;
  }

  // default generating pressure angle
  if (Number.isNaN(genPressureAngleNormal)) genPressureAngleNormal = pressureAngleNormal;

  // convert params with angular units; assumed to be input as degree, not radian
  genPressureAngleNormal = math.angle2rad(genPressureAngleNormal, unitAngle);
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // reference tooth data
  const moduleTransverse = moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const toothThicknessNormal = toothThicknessNormal2({moduleNormal, pressureAngleNormal, profileShiftCoefficient});

  // variables for generation tooling
  let genModuleNormal, genHelixAngle, genProfileShiftCoefficient;
  const isReferenceLead = genPressureAngleNormal === pressureAngleNormal;

  if (isReferenceLead) {
    // generating tool uses reference lead
    genModuleNormal = moduleNormal;
    genHelixAngle = helixAngle;
    genProfileShiftCoefficient = profileShiftCoefficient;
  } else {
    // generating tool uses short or long lead; aka short or long pitch

    // base pitch constraint
    const pitchNormal = pitchNormal1(moduleNormal);
    const basePitchNormal = basePitchNormal1({pitchNormal, pressureAngleNormal});
    const genPitchNormal = pitchNormal2({basePitchNormal, pressureAngleNormal: genPressureAngleNormal});
    genModuleNormal = moduleNormal2(genPitchNormal);

    // helix lead constraint; generated tooth, not cutter tooth
    genHelixAngle = genHelixAngle1({genModuleNormal, numberOfTeeth, helixAngle, theoreticalPitchDiameter});
  }

  // cutter params
  const genPressureAngleTransverse = pressureAngleTransverse1({
    pressureAngleNormal: genPressureAngleNormal,
    helixAngle: genHelixAngle,
  });
  const genModuleTransverse = moduleTransverse1({
    moduleNormal: genModuleNormal,
    helixAngle: genHelixAngle,
  });

  // tooth thickness constraint; ref tooth == gen tooth @ ref dia
  if (!isReferenceLead) {
    const genTheoreticalPitchDiameter = theoreticalPitchDiameter1({
      numberOfTeeth,
      moduleTransverse: genModuleTransverse
    });
    genProfileShiftCoefficient = genProfileShiftCoefficient1({
      numberOfTeeth,
      genPressureAngleNormal,
      genHelixAngle,
      toothThicknessNormal,
      theoreticalPitchDiameter,
      genTheoreticalPitchDiameter
    });
  }

  // compute tight mesh params; meshing of cutter and *generated* tooth
  let workingPressureAngleTransverse = mesh.workingPressureAngleTransverseTheoretical1({
    x1: cutterProfileShiftCoefficient,
    x2: genProfileShiftCoefficient,
    z1: cutterNumberOfTeeth,
    z2: numberOfTeeth,
    alphaN: genPressureAngleNormal,
    alphaT: genPressureAngleTransverse,
  });
  const referenceCenterDistance = mesh.centerDistanceReference2({
    moduleTransverse: genModuleTransverse,
    z1: cutterNumberOfTeeth,
    z2: numberOfTeeth
  });
  const workingCenterDistance = mesh.centerDistanceTheoretical1({
    referenceCenterDistance,
    workingPressureAngleTransverse,
    pressureAngleTransverse: genPressureAngleTransverse,
  });

  // cutter params
  const cutterAddendum = fromCoefficient(cutterAddendumCoefficient, genModuleNormal);
  const cutterDedendum = fromCoefficient(cutterDedendumCoefficient, genModuleNormal);
  const cutterTheoreticalPitchDiameter = theoreticalPitchDiameter1({
    numberOfTeeth: cutterNumberOfTeeth,
    moduleTransverse: genModuleTransverse
  });

  // root diameter of generated tooth
  const cutterTipDiameter = tipDiameter3({
    theoreticalPitchDiameter: cutterTheoreticalPitchDiameter,
    toothAddendum: cutterAddendum,
  });
  let rootDiameter = 2 * workingCenterDistance - cutterTipDiameter;

  // tip diameter of generated tooth
  const cutterRootDiameter = rootDiameter3({
    theoreticalPitchDiameter: cutterTheoreticalPitchDiameter,
    toothDedendum: cutterDedendum,
  });
  const toppingDiameter = 2 * workingCenterDistance - cutterRootDiameter;
  let tipDiameter = workPieceDiameter < toppingDiameter ? workPieceDiameter : toppingDiameter;

  // internal gear; make *positive* for use by forms
  if (internal) {
    rootDiameter = -rootDiameter;
    tipDiameter = -tipDiameter;
  }

  return {
    toothThicknessNormal,
    rootDiameter,
    tipDiameter
  }
}

// endregion

export default {
  coefficient: coefficient,
  fromCoefficient: fromCoefficient,
  moduleNormal1: moduleNormal1,
  moduleNormal2: moduleNormal2,
  moduleTransverse1: moduleTransverse1,
  moduleTransverse2: moduleTransverse2,
  theoreticalPitchDiameter1: theoreticalPitchDiameter1,
  theoreticalPitchDiameter2: theoreticalPitchDiameter2,
  pressureAngleTransverse1: pressureAngleTransverse1,
  pitchNormal1: pitchNormal1,
  pitchNormal2: pitchNormal2,
  basePitchNormal1: basePitchNormal1,
  basePitchTransverse1: basePitchTransverse1,
  pitchAngleTransverse1: pitchAngleTransverse1,
  baseDiameter1: baseDiameter1,
  toothThicknessNormal2: toothThicknessNormal2,
  toothThicknessTransverse1: toothThicknessTransverse1,
  toothThicknessTransverse2: toothThicknessTransverse2,
  toothThicknessTransverse3: toothThicknessTransverse3,
  toothHalfAngleTransverse1: toothHalfAngleTransverse1,
  toothBaseHalfAngleTransverse1: toothBaseHalfAngleTransverse1,
  helixAngleBase1: helixAngleBase1,
  rootDiameter1: rootDiameter1,
  rootDiameter2: rootDiameter2,
  rootDiameter3: rootDiameter3,
  tipDiameter1: tipDiameter1,
  tipDiameter2: tipDiameter2,
  tipDiameter3: tipDiameter3,
  // INV MATH AND CONVERSIONS
  involuteFcn: involuteFcn,
  inverseInvoluteFcn: inverseInvoluteFcn,
  normal2Transverse: normal2Transverse,
  transverse2Normal: transverse2Normal,
  diameter2RollAngle: diameter2RollAngle,
  diameter2RollDistance: diameter2RollDistance,
  rollDistance2Diameter: rollDistance2Diameter,
  diameter2PressureAngleTransverse: diameter2PressureAngleTransverse,
  involuteBezierCoeffs: involuteBezierCoeffs,
  helixRotation: helixRotation,
  // TOOLING
  toothThicknessTransverseFinish1: toothThicknessTransverseFinish1,
  toothThicknessNormalFinish1: toothThicknessNormalFinish1,
  finishStockTransverse1: finishStockTransverse1,
  finishStockNormal1: finishStockNormal1,
  profileShiftCoefficient1: profileShiftCoefficient1,
  profileShiftCoefficient2: profileShiftCoefficient2,
  genProfileShiftCoefficient1: genProfileShiftCoefficient1,
  toothAddendum1: toothAddendum1,
  toothDedendum1: toothDedendum1,
  basicRackAddendum1: basicRackAddendum1,
  basicRackDedendum1: basicRackDedendum1,
  generatingRackData: generatingRackData,
  generatingPinionData: generatingPinionData,
  referenceToothFromRack2: referenceToothFromRack2,
  referenceToothFromPinion1: referenceToothFromPinion1,
}
